import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { fetchSettings } from '../store/slices/settingsSlice';
import { X, ExternalLink, Smartphone } from 'lucide-react';
import { Badge } from './ui/badge';

export const AppDownloadHeaderBanner: React.FC = () => {
  const dispatch = useAppDispatch();
  const { settings } = useAppSelector((state) => state.settings);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isWindowMobile, setIsWindowMobile] = useState<boolean | null>(null);

  useEffect(() => {
    const handleResize = () => {
      const isMobileUA = /android|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(navigator.userAgent);
      const isMobileRes = window.innerWidth <= 768;
      setIsWindowMobile(isMobileUA || isMobileRes);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!settings) {
      dispatch(fetchSettings());
    }
  }, [dispatch, settings]);

  const banner = settings?.appBanner;

  useEffect(() => {
    if (!banner) return;

    // Check frequency rule
    const todayStr = new Date().toISOString().split('T')[0];
    if (banner.showFrequency === 'once_per_day') {
      const lastShownDate = localStorage.getItem('appBannerLastShownDate');
      if (lastShownDate === todayStr) {
        setIsDismissed(true);
        return;
      }
    } else if (banner.showFrequency === 'once_per_session') {
      const dismissed = sessionStorage.getItem('dismissedAppBanner');
      if (dismissed === 'true') {
        setIsDismissed(true);
        return;
      }
    }
  }, [banner]);

  // Don't render if disabled by admin or dismissed
  if (!banner || banner.enabled === false || isDismissed) {
    return null;
  }

  // Device & resolution targeting check
  const target = banner.targetDevice || 'mobile_only';
  if (target === 'mobile_only' && isWindowMobile === false) {
    return null;
  }
  if (target === 'desktop_only' && isWindowMobile === true) {
    return null;
  }

  const playStoreUrl = banner.playStoreUrl || 'https://play.google.com/store/apps/details?id=com.atootbandanmatrimonialapp';
  const text = banner.text || '📱 Download our official Matrimonial App for a faster & better experience!';
  const buttonText = banner.buttonText || 'Download App';
  const badgeText = banner.badgeText || 'GET OUR APP';

  const handleDismiss = () => {
    setIsDismissed(true);
    const todayStr = new Date().toISOString().split('T')[0];
    localStorage.setItem('appBannerLastShownDate', todayStr);
    sessionStorage.setItem('dismissedAppBanner', 'true');
  };

  return (
    <div className="bg-gradient-to-r from-rose-700 via-pink-600 to-purple-700 text-white px-4 py-2.5 shadow-md relative z-50 transition-all duration-300">
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-3 text-xs sm:text-sm">
        {/* Left Side: Badge & Text */}
        <div className="flex items-center gap-2 sm:gap-3 flex-1 overflow-hidden">
          <Badge className="bg-white/20 text-white hover:bg-white/30 border-0 flex items-center gap-1 shrink-0 text-[10px] sm:text-xs font-semibold uppercase tracking-wider px-2 py-0.5">
            <Smartphone className="h-3 w-3 animate-pulse" />
            <span>{badgeText}</span>
          </Badge>
          <p className="truncate font-medium tracking-wide">
            {text}
          </p>
        </div>

        {/* Right Side: Action Button & Close Icon */}
        <div className="flex items-center gap-2 shrink-0">
          <a
            href={playStoreUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleDismiss}
            className="inline-flex items-center gap-1.5 bg-white text-rose-700 hover:bg-rose-50 font-bold px-3 py-1 rounded-full shadow hover:shadow-lg transition-all text-xs active:scale-95 cursor-pointer"
          >
            <span>{buttonText}</span>
            <ExternalLink className="h-3 w-3" />
          </a>
          <button
            onClick={handleDismiss}
            aria-label="Dismiss banner"
            className="p-1 text-white/80 hover:text-white hover:bg-white/20 rounded-full transition-colors focus:outline-none"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AppDownloadHeaderBanner;
